import { animated, useSpring } from '@react-spring/web';
import type { ReactNode } from 'react';
import { memo, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { range } from '#src/lib/arrays.ts';
import { randomIntFromInterval } from '#src/lib/math.ts';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg)
  }
`;

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
  display: inline-flex;
`;

const Svg = styled.svg`
  animation: ${rotate} 1200ms linear 0s infinite;
  z-index: 1;
`;
const SparkleSpan = styled(animated.span)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Sparkle = memo(({ color }: { color: string }) => {
  const size = randomIntFromInterval(6, 16);
  const xPos = randomIntFromInterval(0, 100);
  const yPos = randomIntFromInterval(0, 100);
  const [springConfig] = useState(() => ({
    delay: Math.random() * 2000,
    toDelay: Math.random() * 2000,
  }));

  const pulseSpring = useSpring({
    from: { transform: 'scale(0)' },
    to: [
      { transform: 'scale(1)' },
      { transform: 'scale(0)' },
      { transform: 'scale(0)', delay: springConfig.delay },
    ],
    config: { mass: 1, tension: 250, friction: 26 },
    delay: springConfig.toDelay,
    loop: true,
  });

  return (
    <SparkleSpan
      style={{
        position: 'absolute',
        color,
        top: `calc(${yPos}% - ${size / 2}px)`,
        left: `calc(${xPos}% - ${size / 2}px)`,
        ...pulseSpring,
      }}
    >
      <Svg width={size} height={size} viewBox="0 0 184 184" fill="none">
        <path
          d="M92 0C92 0 96 63.4731 108.263 75.7365C120.527 88 184 92 184 92C184 92 118.527 98 108.263 108.263C98 118.527 92 184 92 184C92 184 86.4731 119 75.7365 108.263C65 97.5269 0 92 0 92C0 92 63.9731 87.5 75.7365 75.7365C87.5 63.9731 92 0 92 0Z"
          fill={color}
        />
      </Svg>
    </SparkleSpan>
  );
});

Sparkle.displayName = 'Sparkle';

type Props = { children: ReactNode; color?: string; nb?: number };

export const Sparkles = ({
  children,
  color = 'var(--c-white)',
  nb = 10,
}: Props) => {
  const prefersReducedMotion = !window.matchMedia(
    '(prefers-reduced-motion: no-preference)'
  ).matches;

  return (
    <Wrapper>
      {!prefersReducedMotion &&
        range(nb).map((_, index) => (
          // oxlint-disable-next-line react/no-array-index-key
          <Sparkle key={index} color={color} />
        ))}

      {children}
    </Wrapper>
  );
};
